import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from './AuthContext';

/**
 * Área que só existe para quem recebeu o bônus.
 *
 * A biblioteca de prompts de IA é liberada pelo administrador pessoa por pessoa.
 * Quem não tem o bônus não vê a página: o menu não mostra o link e, se alguém
 * digitar a URL na mão, a rota devolve a pessoa para a área dela — nada de tela
 * de “recurso bloqueado” entregando que a área existe.
 *
 * A checagem de verdade é sempre no servidor (`/api/prompts` responde 403 sem o
 * bônus), então revogar o acesso vale na hora, mesmo com a página aberta.
 */
export function RotaComBonusPrompts({ children }: { children: React.ReactNode }) {
  const { usuario, carregando, recarregar } = useAuth();
  const local = useLocation();

  /**
   * O bônus é decidido no banco: ao entrar na área perguntamos de novo, para
   * quem acabou de ser liberado não precisar recarregar o navegador.
   *
   * A dependência é o **id** do usuário, e não o objeto `usuario`: reler o perfil
   * devolve um objeto novo, e um efeito que dependesse dele chamaria `recarregar`
   * de novo, em laço — o mesmo defeito que o editor de documentos teve.
   */
  const idDoUsuario = usuario?.id ?? '';
  useEffect(() => {
    if (!carregando && idDoUsuario) void recarregar();
  }, [carregando, idDoUsuario, recarregar]);

  if (carregando || !usuario) {
    if (!carregando && !usuario) {
      return <Navigate to="/entrar" replace state={{ de: local.pathname }} />;
    }
    return (
      <div className="flex min-h-[60vh] items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
        <span className="text-sm">Carregando sua área...</span>
      </div>
    );
  }

  if (!usuario.promptsIA && !usuario.admin) {
    return <Navigate to="/meus-miolos" replace />;
  }

  return <>{children}</>;
}
